/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Button, CircularProgress, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, SxProps, Tooltip, Typography } from "@mui/material";
import { useDashBoard } from "./hooks";
import { byteToGibiByte } from "../../utils/helpers";
import { IGeoRegionTable } from "../../store/api/devices/types";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useReactTable, getCoreRowModel, getPaginationRowModel, getSortedRowModel, createColumnHelper, flexRender, SortingState } from "@tanstack/react-table";
import { TablePagination } from "../../components/ui/table-pagination";
import { SortableHeader } from "../../components/ui/sortable-header";
import StoragePieChart from "../../components/Shared/Charts/StoragePieChart";
import { storageChartData, usedColorCode } from "./helpers";
import { IKPIStorage, Region } from "../../store/api/admin/types";
import { LoginBanner } from "../../components/Administrations/AccessControls/Users/Banner/Banner";
import AlarmsOverviewCards from "../AlarmOverview/AlarmOverviewCards";
import { useGetRadioNodesStatsQuery } from "../../store/api/devices/deviceApiSlice";
import { API } from "../../globals/globals";
import RouterIcon from '@mui/icons-material/Router';
import CardsDisplay from '../../components/Shared/Cards/CardsDisplay';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import MapIcon from '@mui/icons-material/Map';
import DevicesIcon from '@mui/icons-material/Devices';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import ErrorIcon from '@mui/icons-material/Error';
import PublicIcon from '@mui/icons-material/Public';
import { useSelector } from "react-redux";
import { selectedGeoRegionsSelector } from "../../store/api/admin/selectors";
import CellsOverviewCards from "../AlarmOverview/CellsOverviewCards";

const DashboardMap = lazy(() => import("./DashboardMap"));

interface OverviewProps {
  selectedGeoRegions?: string[];
  onAlarmSeveritySelect?: (severity: string) => void;
}

const Overview: React.FC<OverviewProps> = ({ onAlarmSeveritySelect }) => {

  const selectedGeoRegions = useSelector(selectedGeoRegionsSelector) as Region[];
  const regionNames = selectedGeoRegions.map(r => r.name);

  const { nodesCountsData, regionData, handleCellClicked, handleMapMarkerClick, handleDeviceCardClick, localBannerState, setLocalBannerState, dialogTitle } = useDashBoard(regionNames);
  const [mapVisible, setMapVisible] = useState(false);
  const selectedKeys = selectedGeoRegions.map(r => r.objectKey);

  const filteredRegionData = selectedKeys.length ? (regionData || []).filter(region => selectedKeys.includes(region.objectKey)) : regionData || [];


  const { data: radioNodesStats, isLoading: radioNodesLoading } = useGetRadioNodesStatsQuery(regionNames.length ? regionNames : undefined, { pollingInterval: API.POLLING_INTERVAL });

  const colorCodeProps: SxProps = {
    height: "0.80rem",
    width: "0.80rem",
    borderRadius: "3px",
    display: "inline-block",
    mr: 1,
  };

  const checkStorageComparison = (storage: IKPIStorage, type: string) => {
    if (type === "used") {
      if (storage === undefined) return;
      const { used, total } = storage;
      if (Number(used) > Number(total)) {
        return byteToGibiByte(used);
      } else if (Number(used) < 0) {
        return byteToGibiByte(0);
      } else return byteToGibiByte(used);
    } else {
      if (storage === undefined) return;
      const { available, used, total } = storage;
      if (Number(available) < 0) {
        return byteToGibiByte(0);
      } else if (Number(used) > Number(total)) {
        return byteToGibiByte(0);
      } else return byteToGibiByte(available);
    }
  };

  const location = useLocation();

  useEffect(() => {
    const previousRoute = localStorage.getItem("currentRoute");
    if (previousRoute) {
      localStorage.setItem("previousRoute", previousRoute);
    }
    localStorage.setItem("currentRoute", location.pathname);

  }, [location]);

  const getStorageLegend = (storageData: IKPIStorage) => (
    <>
      <Grid item>{storageChartData(storageData).length > 0 && <StoragePieChart data={storageChartData(storageData)} />}</Grid>
      <Grid item mt={{ xs: 4 }}>
        <Grid container columnGap={3} direction={"row"} justifyContent={"center"} alignItems={"center"}>
          <Grid item>
            <Typography variant="body2">
              <Typography component="span" sx={{ ...colorCodeProps, backgroundColor: "var(--success)" }}></Typography>
              Free
            </Typography>
            <Typography variant="body1" sx={{ minWidth: "70px", textWrap: "nowrap" }}>{`${checkStorageComparison(
              storageData!,
              "free"
            )} GB`}</Typography>
            {/* <Typography variant="body1" sx={{ minWidth: "70px", textWrap: "nowrap" }}>{`${byteToGibiByte(storageData?.available)} GB`}</Typography> */}
          </Grid>
          <Divider orientation="vertical" variant="middle" flexItem />

          <Grid item>
            <Typography variant="body2">
              <Typography component="span" sx={{ ...colorCodeProps, backgroundColor: usedColorCode(storageData!) }}></Typography>
              Used
            </Typography>
            <Typography variant="body1" sx={{ minWidth: "70px", textWrap: "nowrap" }}>{`${checkStorageComparison(
              storageData!,
              "used"
            )} GB`}</Typography>
            {/* <Typography variant="body1" sx={{ minWidth: "70px", textWrap: "nowrap" }}>{`${byteToGibiByte(storageData?.used)} GB`}</Typography> */}
          </Grid>
          <Divider orientation="vertical" variant="middle" flexItem />

          <Grid item>
            <Typography variant="body2">Total</Typography>
            <Typography variant="body1" sx={{ minWidth: "70px", textWrap: "nowrap" }}>{`${byteToGibiByte(storageData?.total)} GB`}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  );

  return (
    <>
      <Dialog open={localBannerState} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: "var(--background-default)", color: "var(--foreground)" }}>{dialogTitle}</DialogTitle>
        <DialogContent sx={{ backgroundColor: "var(--background-default)", color: "var(--foreground)" }}>
          <LoginBanner />
          <Button variant="contained" size="medium" color="secondary" sx={{ float: "right" }}
            onClick={() => { setLocalBannerState(false) }}>OK</Button>
        </DialogContent>
      </Dialog>
      <Grid
        container
        rowGap={{ md: 6, sm: 5, xs: 5 }}
        direction={"row"}
        px={7}
        sx={{ "& .MuiPaper-root": { backgroundColor: "var(--card)" }, backgroundColor: "var(--color-background)", position: "relative" }}
        alignItems={"center"}
        columnGap={{ md: 4, sm: 2, xs: 2 }}
      >
        <Grid item md={12} xs={12} sm={12} >
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-foreground mb-0 flex items-center gap-2">
              <SmartphoneIcon sx={{ fontSize: 20 }} className="text-[var(--muted-foreground)]" />
              Devices
            </h2>
            <Tooltip title={mapVisible ? 'Hide map' : 'Show map'}>
              <IconButton
                size="small"
                onClick={() => setMapVisible((v) => !v)}
                sx={{
                  backgroundColor: mapVisible ? 'var(--accent)' : 'transparent',
                  color: mapVisible ? 'var(--dashboard-map-icon-active)' : 'var(--dashboard-map-icon-inactive)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius, 0.5rem)',
                  boxShadow: 'var(--shadow-card)',
                  width: 36,
                  height: 36,
                  '&:hover': { backgroundColor: 'var(--accent)' },
                }}
              >
                <MapIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </div>
        </Grid>
        {mapVisible && regionData !== undefined && regionData.length > 0 && (
          <Grid item xs={12}>
            <Suspense
              fallback={
                <div className="flex justify-center items-center" style={{ minHeight: 400 }}>
                  <CircularProgress />
                </div>
              }
            >
              <DashboardMap
                regionData={filteredRegionData}
                handleRegionClick={handleMapMarkerClick}
                selectedGeoRegions={selectedKeys}
                isOpen={mapVisible}
              />
            </Suspense>
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={3}>
              <CardsDisplay
                title="Total"
                titleRight={<DevicesIcon sx={{ fontSize: 16, color: 'var(--foreground)' }} />}
                sx={{ backgroundColor: 'var(--card)', height: '100%' }}
                onClick={() => handleDeviceCardClick("total")}
                ariaLabel="View total devices in Nodes"
                contents={
                  nodesCountsData?.isLoading ? (
                    <div className="flex justify-center items-center" style={{ minHeight: 50 }}><CircularProgress size={24} /></div>
                  ) : (
                    <div className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--foreground)' }}>
                      {nodesCountsData?.data?.totals?.allDeviceCount ?? 0}
                    </div>
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardsDisplay
                title="Connected"
                titleRight={<LinkIcon sx={{ fontSize: 16, color: 'var(--success)' }} />}
                sx={{ backgroundColor: 'var(--card)', height: '100%' }}
                onClick={() => handleDeviceCardClick("connected")}
                ariaLabel="View connected devices in Nodes"
                contents={
                  nodesCountsData?.isLoading ? (
                    <div className="flex justify-center items-center" style={{ minHeight: 50 }}><CircularProgress size={24} /></div>
                  ) : (
                    <div className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--foreground)' }}>
                      {(nodesCountsData?.data?.totals?.allDeviceCount ?? 0) - (nodesCountsData?.data?.totals?.disconnectedDeviceCount ?? 0)}
                    </div>
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardsDisplay
                title="Disconnected"
                titleRight={<LinkOffIcon sx={{ fontSize: 16, color: 'var(--destructive)' }} />}
                sx={{ backgroundColor: 'var(--card)', height: '100%' }}
                onClick={() => handleDeviceCardClick("disconnected")}
                ariaLabel="View disconnected devices in Nodes"
                contents={
                  nodesCountsData?.isLoading ? (
                    <div className="flex justify-center items-center" style={{ minHeight: 50 }}><CircularProgress size={24} /></div>
                  ) : (
                    <div className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--foreground)' }}>
                      {nodesCountsData?.data?.totals?.disconnectedDeviceCount ?? 0}
                    </div>
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <CardsDisplay
                title="KPI Sync Error"
                titleRight={<SyncProblemIcon sx={{ fontSize: 16, color: 'var(--error)' }} />}
                sx={{ backgroundColor: 'var(--card)', height: '100%' }}
                onClick={() => handleDeviceCardClick("kpiSyncError")}
                ariaLabel="View KPI sync error devices in Nodes"
                contents={
                  nodesCountsData?.isLoading ? (
                    <div className="flex justify-center items-center" style={{ minHeight: 50 }}><CircularProgress size={24} /></div>
                  ) : (
                    <div className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--foreground)' }}>
                      {nodesCountsData?.data?.totals?.kpiSyncErrorCount ?? 0}
                    </div>
                  )
                }
              />
            </Grid>
          </Grid>
        </Grid>

        {/**Second Row - Radio Nodes------------------------------------------------ */}
        <Grid item md={12} xs={12} sm={12}>
          <h2 className="text-lg font-semibold text-foreground mb-0 flex items-center gap-2">
            <RouterIcon sx={{ fontSize: 20 }} className="text-[var(--muted-foreground)]" />
            Radio Nodes
          </h2>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <CardsDisplay
                title="Total"
                titleRight={<DeviceHubIcon sx={{ fontSize: 16, color: 'var(--foreground)' }} />}
                sx={{ backgroundColor: 'var(--card)', height: '100%' }}
                contents={
                  radioNodesLoading ? (
                    <div className="flex justify-center items-center" style={{ minHeight: 50 }}><CircularProgress size={24} /></div>
                  ) : (
                    <div className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--foreground)' }}>
                      {radioNodesStats?.totalRadioNodes || 0}
                    </div>
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CardsDisplay
                title="Connected"
                titleRight={<LinkIcon sx={{ fontSize: 16, color: 'var(--success)' }} />}
                sx={{ backgroundColor: 'var(--card)', height: '100%' }}
                contents={
                  radioNodesLoading ? (
                    <div className="flex justify-center items-center" style={{ minHeight: 50 }}><CircularProgress size={24} /></div>
                  ) : (
                    <div className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--foreground)' }}>
                      {radioNodesStats?.connectedRadioNodes || 0}
                    </div>
                  )
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <CardsDisplay
                title="Disconnected"
                titleRight={<LinkOffIcon sx={{ fontSize: 16, color: 'var(--destructive)' }} />}
                sx={{ backgroundColor: 'var(--card)', height: '100%' }}
                contents={
                  radioNodesLoading ? (
                    <div className="flex justify-center items-center" style={{ minHeight: 50 }}><CircularProgress size={24} /></div>
                  ) : (
                    <div className="text-2xl font-semibold tracking-tight" style={{ color: 'var(--foreground)' }}>
                      {radioNodesStats?.disconnectedRadioNodes || 0}
                    </div>
                  )
                }
              />
            </Grid>
          </Grid>
        </Grid>


        {/* ********************************************************************************* */}

        <Grid item md={12} xs={12} sm={12}>
          <h2 className="text-lg font-semibold text-foreground mb-0 flex items-center gap-2">
            <ErrorIcon sx={{ fontSize: 20 }} className="text-[var(--muted-foreground)]" />
            Alarms
          </h2>
        </Grid>
        <Grid item xs={12}>
          <AlarmsOverviewCards onSeveritySelect={onAlarmSeveritySelect} />
        </Grid>

        {/*******CELLS**********************************/}
        <Grid item md={12} xs={12} sm={12}>
          <h2 className="text-lg font-semibold text-foreground mb-0 flex items-center gap-2">
            Cells
          </h2>
        </Grid>
        <Grid item xs={12}>
          <CellsOverviewCards />
        </Grid>
        <Grid item container md={16} pt={3}>
          {regionData !== undefined && regionData?.length > 0 && (<>
            <Grid item md={12} xs={12} sm={12}>
              <h2 className="text-lg font-semibold text-foreground mb-0 flex items-center gap-2">
                <PublicIcon sx={{ fontSize: 20 }} className="text-[var(--muted-foreground)]" />
                Regions
              </h2>
              <Typography variant="body2" sx={{ color: "var(--muted-foreground)", mt: 1, ml: 6 }}>
                Tip: Click any cell to jump to Nodes with that filter applied.
              </Typography>
            </Grid>
            <GeoRegionTable regionData={filteredRegionData} handleCellClicked={handleCellClicked} />
          </>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const columnHelper = createColumnHelper<IGeoRegionTable>();

const geoRegionColumns = [
  columnHelper.accessor("name", {
    header: ({ column }) => <SortableHeader column={column}>Region</SortableHeader>,
    size: 350,
    cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
  }),
  columnHelper.accessor("deviceCount", {
    header: ({ column }) => <div className="flex justify-end"><SortableHeader column={column}>Devices</SortableHeader></div>,
    size: 200,
    cell: ({ getValue }) => <div className="text-right tabular-nums">{getValue() ?? 0}</div>,
  }),
  columnHelper.accessor("disconnectedDeviceCount", {
    header: ({ column }) => <div className="flex justify-end"><SortableHeader column={column}>Disconnected Devices</SortableHeader></div>,
    size: 320,
    cell: ({ getValue }) => <div className="text-right tabular-nums">{getValue() ?? 0}</div>,
  }),
  columnHelper.accessor("offlineDeviceCount", {
    header: ({ column }) => <div className="flex justify-end"><SortableHeader column={column}>Offline Devices</SortableHeader></div>,
    size: 320,
    cell: ({ getValue }) => <div className="text-right tabular-nums">{getValue() ?? 0}</div>,
  }),
  columnHelper.accessor("kpiSyncErrorCount", {
    header: ({ column }) => <div className="flex justify-end"><SortableHeader column={column}>KPI Error</SortableHeader></div>,
    size: 320,
    cell: ({ getValue }) => <div className="text-right tabular-nums">{getValue() ?? 0}</div>,
  }),
];

function GeoRegionTable({ regionData, handleCellClicked }: { regionData: IGeoRegionTable[]; handleCellClicked: (params: any) => void }) {
  const data = useMemo(() => regionData, [regionData]);
  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data,
    columns: geoRegionColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
    initialState: { pagination: { pageSize: 10 } },
  });

  return (
    <div className="w-full mt-5 overflow-hidden rounded-lg border bg-card text-card-foreground">
      <Table className="w-full caption-bottom text-sm" style={{ borderCollapse: 'collapse' }}>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="px-4 py-3 h-12 whitespace-nowrap"
                  style={{ width: header.getSize() }}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="[&_tr:last-child]:border-border">
          {table.getRowModel().rows.map((row) => (
            <TableRow
              key={row.original.objectKey}
              className="cursor-pointer group"
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className="px-4 py-3"
                  onClick={() => handleCellClicked({ row: row.original, field: cell.column.id })}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <TablePagination table={table} className="mt-4 justify-end" />
    </div>
  );
}

export default Overview;
